export function createCanvas(w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h

  return canvas
}

/**
 * Resize a canvas to the given width and height
 * @param  {object} canvas The source canvas
 * @param  {number} width The target width
 * @param  {number} height The target height
 * @returns {CanvasRenderingContext2D} The resulting 2d context
 */
export function resizeCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): CanvasRenderingContext2D {
  let processCanvas: HTMLCanvasElement
  let context: CanvasRenderingContext2D | null

  if (canvas.width === width && canvas.height === height) {
    processCanvas = canvas
    context = processCanvas.getContext('2d')
  } else {
    processCanvas = createCanvas(width, height)
    context = processCanvas.getContext('2d')
    if (!context) throw new Error('Could not get 2d context from canvas')
    context.drawImage(canvas, 0, 0, width, height)
  }

  if (!context) throw new Error('Could not get 2d context from canvas')

  return context
}

/**
 * Change the image to blank and white using a simple threshold
 *
 * @param  {object} image The imageData of a Canvas 2d context
 * @param  {number} threshold Threshold value (0-255)
 * @return {ImageData} The resulting imageData
 */
export function canvasThreshold(image: ImageData, threshold: number): ImageData {
  for (let i = 0; i < image.data.length; i += 4) {
    const luminance = image.data[i] * 0.299 + image.data[i + 1] * 0.587 + image.data[i + 2] * 0.114

    const value = luminance < threshold ? 0 : 255
    image.data.fill(value, i, i + 3)
  }

  return image
}

/**
 * Change the image to grayscale
 *
 * @param  {object} image The imageData of a Canvas 2d context
 * @param  {array} background Three values consisting of the r, g, b of the background
 * @return {ImageData} The resulting imageData
 */
export function canvasFlatten(image: ImageData, background: [number, number, number]): ImageData {
  for (let i = 0; i < image.data.length; i += 4) {
    const alpha = image.data[i + 3]
    const invAlpha = 255 - alpha

    image.data[i] = (alpha * image.data[i] + invAlpha * background[0]) / 255
    image.data[i + 1] = (alpha * image.data[i + 1] + invAlpha * background[1]) / 255
    image.data[i + 2] = (alpha * image.data[i + 2] + invAlpha * background[2]) / 255
    image.data[i + 3] = 0xff
  }

  return image
}
